import styles from "./Showcase.module.css";
import { createContext, FC, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FilterOption, FilterResults, FilterWrapper, TResult } from "components/organisms";
import { axiosInstance } from "api";
import { toast } from "react-hot-toast";
import _ from "lodash";
import queryString from "query-string";
import { sanatiseStr, useDeviceClassification } from "utils";
import { getReferrer } from "utils/getReferrer";
import classNames from "classnames";
import { motion } from "framer-motion";
import { PostSchema, QueryStringSchema, URLQueryStringSchema } from "./meta/apiScema";
import hamburger from "assets/SVG/Hamburger.svg";
import tiles from "assets/SVG/Tiles.svg";
import chevron from "assets/SVG/VectorChevronRight.svg";
import { getLocalAsset } from "utils/getLocalAsset";


/* Types */
type TShowCaseContentProps = {
  InitialSubpages: any,
  FiltersCollection: { [key: string]: any },
  ShowLayoutToggle: boolean,
  ShowPublicationDateReadTimeContentType: boolean
}

type TFilterStateProps = { [key: string]: string[] }

/* Context */
export const FilterContext = createContext<SetStateAction<any>>({});

export const ShowCaseContent: FC<TShowCaseContentProps> = ({
  InitialSubpages,
  FiltersCollection,
  ShowLayoutToggle,
  ShowPublicationDateReadTimeContentType
}) => {

  const { isDesktop } = useDeviceClassification();

  // Page State
  const [firstRender, setFirstRender] = useState<boolean>(false);
  const [layoutConfig, setLayoutConfig] = useState<'Large-Card' | '3-Col'>('Large-Card');

  // Filter Results State
  const [filterState, setFilterState] = useState<null | TFilterStateProps>(null);
  const [filterResults, setFilterResults] = useState<null | TResult[]>(null);
  const [similarFilterResults, setSimilarFilterResults] = useState<null | TResult[]>(null);
  const [filterResultsReset, setFilterResultsReset] = useState<boolean>(false);
  const [similarResultsDisplayed, setSimilarResultsDisplayed] = useState<boolean>(false);
  const [allowedFilters, setAllowedFilters] = useState<string[] | null>(null);
  const [filterSet, setFilterSet] = useState<{ [key: string]: any }>(null);
  const [queryParamIndex, setQueryParamIndex] = useState<{ [key: string]: any }>(null);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Error State
  const [error, setError] = useState<string | boolean>(false);
  const [loadMoreError, setLoadMoreError] = useState<string | boolean>(false);

  // Skip State
  const [skip, setSkip] = useState<number>(0);

  // Loading States
  const [loadingMore, setLoadingMore] = useState<boolean>(true);
  const [loadingResults, setLoadingResults] = useState<boolean>(true);
  const [requestedMoreResults, setRequestedMoreResults] = useState<boolean>(false);

  const displayThreeColLayout = layoutConfig === '3-Col';

  /* Process and Generate FilterSet and MetaData*/
  useEffect(() => {
    setAllowedFilters(FiltersCollection?.VisibleFilters.map(prop => prop.Name))
  }, []);

  useEffect(() => {
    if (allowedFilters) {
      let filters = {};
      const otherFilters = FiltersCollection?.OthersFilter;

      allowedFilters.forEach((key) => {
        filters[key] = FiltersCollection[key + 'Filter'] || otherFilters[otherFilters.findIndex((obj) => obj.Name === key)]?.Tags
      });

      setFilterSet(filters)
    }
  }, [allowedFilters]);

  useEffect(() => {
    if (filterSet) {
      let queryParametersMap = {};
      Object.keys(filterSet).forEach((key) => {
        const k = QueryStringSchema[key]
        queryParametersMap[k] = PostSchema[key]
      });

      setQueryParamIndex(queryParametersMap)
    }

  }, [filterSet])

  const handleSetLayoutConfig = (string: 'Large-Card' | '3-Col') => {
    setLayoutConfig(string);
    setLoadingResults(true);
    setTimeout(() => {
      setLoadingResults(false);
    }, 1000)
  }

  /*
    Grab and use any queryParameters in the URL if present
  */
  useEffect(() => {
    if (queryParamIndex) {

      let queryParams: any = queryString.parse(window.location.search);
      if (_.keys(queryParams).length) {

        _.keys(queryParams).forEach(key => {
          queryParams[queryParamIndex[key] || PostSchema[URLQueryStringSchema[key]]] = _.words(queryParams[key], /[^,]+/g).map(str => (str.toLowerCase()))
          delete queryParams[key]
        })

        setFilterState(queryParams);
      }

      const coordinates = document.getElementsByClassName('filterContentWrapper')[0].getBoundingClientRect().top
      Object.keys(queryParams).length && window.scrollTo(0, coordinates)

      setFirstRender(true);
      return () => setFirstRender(false);
    }
  }, [queryParamIndex]);


  /**
   * Request more results when filters are updated.
  */
  useEffect(() => {
    if (firstRender) {
      setLoadingResults(true);
      // Usecase: Scroll Results into view | data: coordinates
      const targetScrollDesktop = document.getElementById('page')?.getClientRects()[0].top
      const targetScrollMobile = document.getElementById('projects')?.getClientRects()[0].top;
      const heroHeight = document.getElementById('hero')?.getClientRects()[0].height;
      const heroGallery = document.getElementById('hero-gallery')?.getClientRects()[0].height;

      /**
       * On page load don't scroll results into view.
       * If user has scrolled down the page, signalling page load complete, scroll results into view.
       * If user is on 800px < screen size, adjust scrolling logic.
       * */
      if (isDesktop) {
        const shouldScrollResultsIntoViewDesktop = filterState && window.scrollY > heroHeight / 2;
        shouldScrollResultsIntoViewDesktop && window.scrollTo(0, window.scrollY + targetScrollDesktop);
        document.getElementById('main-navigation')?.classList.remove('active');
      } else {
        const shouldScrollResultsIntoViewMobile = filterState && window.scrollY > heroGallery / 2;
        shouldScrollResultsIntoViewMobile && window.scrollTo(0, window.scrollY + targetScrollMobile - 10);
        document.getElementById('main-navigation')?.classList.remove('active');
      }

      debounceLoadMoreResults(filterState);
    }

  }, [filterState, firstRender]);


  // Toggle all loaders
  const toggleLoaders = (bool: boolean) => {
    setLoadingMore(bool);
    setLoadingResults(bool);
  };


  /** Extract relevant data from payload */
  const extractResults = (data: any) => {
    try {
      const results = data.map((value: any) => ({
        Name: sanatiseStr(value.MenuTitle),
        Url: value.Url,
        Abstract: sanatiseStr(value.Abstract),
        Img: value.HeroSectionImage1?.Src?.length ? value.HeroSectionImage1 : value.LargeBlockImage,
        ShowPublicationDateReadTimeContentType: value.ShowPublicationDateReadTimeContentType,
        ReadTime: value.ReadTime,
        PublicationDate: value.PublicationDate,
        ContentType: value.ContentType,
        FeaturedText: value.FeaturedText
      }))
      return results
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    if (requestedMoreResults) {
      setLoadingMore(true);
      debounceLoadMoreResults(filterState);
    }

  }, [requestedMoreResults])

  /**
 * 1. On mobile layout is stacked vertically.
 * 2. When a new filterset is selected, results needs to be scrolled into view.
*/
  const scrollResultsIntoMobileView = () => {
    if (!isDesktop && !requestedMoreResults) {
      setTimeout(() => {
        const targetScroll = document.getElementById('projects').getBoundingClientRect().top;
        const heroGallery = document.getElementById('hero-gallery')?.getClientRects()[0].height;
        const shouldScrollResultsIntoViewMobile = filterState && window.scrollY > heroGallery / 2;
        shouldScrollResultsIntoViewMobile && window.scrollTo(0, window.scrollY + targetScroll);
      }, 500)
    }
  };

  /**
   * 1. Set Loading to true.
   * 2. Build query param string.
   * 3. Add or replace results conditionally.
  */
  const loadMoreResults = async (filterState) => {
    if (process.env.NODE_ENV === "development") {
      console.log('Entering API Call', {
        error: error,
        requestedMoreResults: requestedMoreResults,
        filterState: filterState
      })
    };

    const isFilterStateEmpty = filterState ? Math.max(...Object.keys(filterState).map(f => filterState[f].length)) < 1 : true;
    const filters = filterState && !isFilterStateEmpty && Object.keys(filterState).map(f => {
      return `${f}=${filterState[f]}`
    });

    try {
      const { statusText, status, data } = await axiosInstance.post('api/sitecore/showcase/loadsubpages', filters ? {
        ...filterState,
        count: 9,
        skip: skip,
        referrer: getReferrer()
      }
        : {
          "count": 9,
          "skip": skip,
          referrer: getReferrer()
        });

      const similarResults = data.SimilarResults && extractResults(data.SimilarResults);

      if (statusText === 'OK') {
        if (data.Results.length) {
          !isDesktop && toast.success('Your request was succesful!')
          const results = extractResults(data.Results);

          if (requestedMoreResults) {
            setFilterResults([...filterResults, ...results]);
          } else {
            setFilterResults(results)
          }

          results && setSkip(skip + 10);
        }
        else if (requestedMoreResults) {
          setLoadMoreError("There are no more results!")
        } else {
          similarResults && setFilterResults(similarResults)
          !requestedMoreResults && setError("No results found. Try changing your filter selection.");
        }
      }
      else {
        console.log(`The server responded with a ${status} - ${statusText}`)
        setError('No results found. Try changing your filter selection.')
      }

      !isDesktop && scrollResultsIntoMobileView()
      setRequestedMoreResults(false);
      setSimilarResultsDisplayed(false);
      toggleLoaders(false);
      similarResults && setSimilarFilterResults(similarResults)
    } catch (e) {
      console.log(e)
      toast.error("This didn't work.")
      setError(e);
      toggleLoaders(false);
    }
  };

  // Handle load more results
  const handleLoadMore = () => {
    setRequestedMoreResults(true);
  };

  // Take latest request for data in que after 2.5s
  const debounceLoadMoreResults = useCallback(_.debounce(loadMoreResults, 2500), [skip, requestedMoreResults]);

  // Handle filter state change
  const handleFilterState = (d: any) => {
    setError(false);
    setRequestedMoreResults(false);
    setFilterState(d);
    setSkip(0);
    setLoadMoreError(false);
  };

  // Append similar results to the existing results collection
  const handleShowSimilarResults = () => {
    similarFilterResults && setFilterResults([...filterResults, ...similarFilterResults]);
    setSimilarResultsDisplayed(true)
  }

  const handleSetShowFilters = (bool: boolean) => {
    setShowFilters(bool);
    setLoadingResults(true);
    setTimeout(() => {
      setLoadingResults(false);
    }, 700)
  }

  const value = useMemo(() => ({
    loadMoreError,
    filterResultsReset,
    filterResults,
    filterState,
    loadingMore,
    loadingResults,
    error,
    similarResultsDisplayed,
    layoutConfig,
    handleFilterState: (d: any) => handleFilterState(d),
    handleLoadResults: () => handleLoadMore(),
    showSimilarResults: () => handleShowSimilarResults()
  }), [
    similarResultsDisplayed,
    filterResults,
    similarFilterResults,
    filterState,
    loadingMore,
    loadingResults,
    filterResultsReset,
    error,
    loadMoreError,
    layoutConfig
  ]);

  const filterSetNodes = useMemo(() => {
    const len = filterSet && Object.keys(filterSet).length;
    return filterSet && Object.keys(filterSet).map((f, i) => {
      return <FilterOption
        key={'filter-option-' + f}
        iconType="default"
        label={[PostSchema[f], f]}
        list={filterSet[f]}
        lastOfType={i === len - 1}
      />
    });
  }, [filterSet]);

  const threeColLayout = layoutConfig === '3-Col' && isDesktop;

  return (
    <FilterContext.Provider value={value}>

      {/* Content Bar */}
      <div className={styles.contentBar}>
        {displayThreeColLayout ?
          <motion.button
            animate={{ color: showFilters ? '#84bd00' : '#666666' }}
            className={classNames(styles.chevronBtn)}
            onClick={() => handleSetShowFilters(!showFilters)}>Filters
            <motion.span
              className={styles.chevron}
              animate={{
                display: 'inline-flex',
                rotate: showFilters ? 90 : 0,
                y: 1,
                x: 3
              }}
            ><img src={getLocalAsset(chevron)} alt="Filter section - chevron icon" /></motion.span>
          </motion.button>
          :
          <button className={classNames(styles.chevronBtn)}>Filters</button>
        }
        {ShowLayoutToggle && isDesktop &&
          <div className={styles.layoutToggles}>
            <button
              onClick={() => handleSetLayoutConfig('Large-Card')}
              className={classNames(styles.layoutIcon, { active: layoutConfig === 'Large-Card' })}>
              <img src={getLocalAsset(hamburger)} alt="two column display selector" /></button>
            <button
              onClick={() => handleSetLayoutConfig('3-Col')}
              className={classNames(styles.layoutIcon, { active: layoutConfig === '3-Col' })}>
              <img src={getLocalAsset(tiles)} alt="three column display selector" /></button>
          </div>
        }
      </div>

      {/* Content Container */}
      <div className={styles.contentContainer}>

        {filterSet && <FilterWrapper
          isDesktop={isDesktop}
          className={classNames({ active: showFilters })}
          layoutConfig={layoutConfig}
          showFilters={showFilters}>
          {filterSetNodes}
        </FilterWrapper>
        }

        <motion.div
          id='projects'
          className={classNames(styles.projects, { hideFilter : layoutConfig === '3-Col' && !showFilters })}
        >
          <div className={styles.filterResultsWrapper}>
            <FilterResults
              showFilters={showFilters}
              _ShowPublicationDateReadTimeContentType={ShowPublicationDateReadTimeContentType}
              layoutConfig={layoutConfig}
              initialSubpages={InitialSubpages} />
          </div>
        </motion.div>

      </div>

    </FilterContext.Provider>
  );
};


export const useFilterContext = () => {
  const context = useContext(FilterContext);

  if (!context) throw new Error("Item must be used in FilterContext");

  return context;
}

